import { inject } from '@angular/core';
import { marker as _ } from '@colsen1991/ngx-translate-extract-marker';
import { AntragsType } from '@ebeihilfe/core';
import { KurzantragConfig } from '@ebeihilfe/einreichung-old';
import { LegacyFeldTyp } from '@eBeihilfe/einreichung-shared/enums';
import { CheckboxElement, DatepickerElement, ParagraphElement, RadioElement, TextboxElement } from '@ebeihilfe/forms';
import { TranslateService } from '@ngx-translate/core';

export const getKurzantragConfig = (): KurzantragConfig => {
  const translate = inject(TranslateService);

  return <KurzantragConfig>{
    formlyConfig: [
      new RadioElement({
        key: 'antragsArt',
        required: true,
        options: [
          {
            label: _('kurzantrag.antrags_art.kurzantrag'),
            value: AntragsType.KURZANTRAG,
            key: AntragsType[AntragsType.KURZANTRAG],
          },
          {
            label: _('kurzantrag.antrags_art.langantrag'),
            value: AntragsType.LANGANTRAG_LEGACY,
            key: AntragsType[AntragsType.LANGANTRAG],
          },
        ],
      }),
      new CheckboxElement({
        key: 'rente',
        value: false,
        label: _('kurzantrag.rente.checkbox'),
        hide: (model: { antragsArt: AntragsType }) => model?.antragsArt !== AntragsType.KURZANTRAG,
      }),
      new RadioElement({
        key: 'rentenMitteilung',
        hide: (model: { rente: boolean; antragsArt: AntragsType }) => model?.antragsArt !== AntragsType.KURZANTRAG || !model?.rente,
        requiredIf: 'rente',
        options: [
          {
            label: _('kurzantrag.rente.beigefügt'),
            value: 'istBeigefuegt',
            key: 'istBeigefuegt',
          },
          {
            label: _('kurzantrag.rente.liegtvor'),
            value: 'liegtvor',
            key: 'liegtvor',
          },
        ],
      }),
      new DatepickerElement({
        key: 'versorgungsempfaengerSeit',
        hide: (model: { antragsArt: AntragsType }) => model?.antragsArt !== AntragsType.KURZANTRAG,
        value: null as Date,
        label: _('kurzantrag.rente.versorgungsempfaengerSeit'),
        hint: _('kurzantrag.rente.datum_format'),
      }),
      new TextboxElement<number>({
        key: 'gesamtbetrag',
        type: 'number',
        hide: (model: { antragsArt: AntragsType }) => model?.antragsArt !== AntragsType.KURZANTRAG,
        min: '0',
        max: '99999',
        label: _('kurzantrag.gesamtbetrag'),
        errors: [
          {
            errorCode: 'min',
            errorMessage: translate.instant(_('kurzantrag.errorMessage.min_gesamtBetrag'), { min: 0 }),
          },
          {
            errorCode: 'max',
            errorMessage: translate.instant(_('kurzantrag.errorMessage.max_gesamtBetrag'), { max: 99999 }),
          },
        ],
      }),
      new TextboxElement<number>({
        key: 'anzBelege',
        type: 'number',
        hide: (model: { antragsArt: AntragsType }) => model?.antragsArt !== AntragsType.KURZANTRAG,
        min: '0',
        max: '999',
        label: _('kurzantrag.anz_belege'),
        errors: [
          {
            errorCode: 'min',
            errorMessage: translate.instant(_('kurzantrag.errorMessage.min_anzahlBelege'), { min: 0 }),
          },
          {
            errorCode: 'max',
            errorMessage: translate.instant(_('kurzantrag.errorMessage.max_anzahlBelege'), { max: 999 }),
          },
        ],
      }),
      new ParagraphElement({
        label: _('kurzantrag.infotext_telearbeit'),
        key: 'infoTextTelearbeit',
        hide: (model: { antragsArt: AntragsType }) => model?.antragsArt !== AntragsType.KURZANTRAG,
      }),
      new CheckboxElement({
        key: 'widerspruchTelearbeit',
        value: false,
        hide: (model: { antragsArt: AntragsType }) => model?.antragsArt !== AntragsType.KURZANTRAG,
        label: _('kurzantrag.widerspruchTelearbeit'),
      }),
    ],
    meta: [
      {
        keyDom: 'antragsArt',
        keyInternal: 'antragsArt',
        type: LegacyFeldTyp.TEXT,
        valueFromForm: (model) => model['antragsArt'] as string,
      },
      {
        keyDom: 'widerspruchTelearbeit',
        keyInternal: 'WiderspruchTelearbeit',
        type: LegacyFeldTyp.CHECKBOX,
        valueFromForm: (model) => model['widerspruchTelearbeit'] as string,
      },
      {
        keyDom: 'rente',
        keyInternal: 'Rente',
        type: LegacyFeldTyp.CHECKBOX,
        valueFromForm: (model) => model['rente'] as string,
      },
      {
        keyDom: 'liegtVor',
        keyInternal: 'LiegtVor',
        type: LegacyFeldTyp.CHECKBOX,
        valueFromForm: (model) => model['rentenMitteilung'] === 'liegtvor',
      },
      {
        keyDom: 'istBeigefuegt',
        keyInternal: 'IstBeigefuegt',
        type: LegacyFeldTyp.CHECKBOX,
        valueFromForm: (model) => model['rentenMitteilung'] === 'istBeigefuegt',
      },
      {
        keyDom: 'versorgungsempfaengerSeit',
        keyInternal: 'VersorgungsempfaengerSeit',
        type: LegacyFeldTyp.TEXT,
        valueFromForm: (model) => model['versorgungsempfaengerSeit'] ?? '',
      },
      {
        keyDom: 'gesamtbetrag',
        keyInternal: 'Gesamtbetrag',
        type: LegacyFeldTyp.TEXT,
        valueFromForm: (model) => model['gesamtbetrag'],
      },
      {
        keyDom: 'anzBelege',
        keyInternal: 'AnzahlBelege',
        type: LegacyFeldTyp.TEXT,
        valueFromForm: (model) => model['anzBelege'],
      },
      {
        keyDom: 'eingangsdatum',
        keyInternal: 'Eingangsdatum',
        type: LegacyFeldTyp.TEXT,
        valueFromForm: () => new Date().toLocaleDateString('de-DE', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        }),
      },
    ],
  };
};
